import { css } from '@emotion/react'
import { ConsultaSeguimentoArrowDown } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoArrowDown'
import { ConsultaSeguimentoIndicatorHelper } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoPerguntaHeader'
import { PerguntaSimNaoRadioGroupBoxField } from '@mchat-rf/consulta-seguimento/components/PerguntaSimNaoRadioGroupBoxField'
import { PerguntaSimNaoRadioListField } from '@mchat-rf/consulta-seguimento/components/PerguntaSimNaoRadioListField'
import { isPerguntaStatusRespondendo } from '@mchat-rf/consulta-seguimento/consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { useConsultaSeguimentoAutoScroll } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoAutoScroll'
import { useConsultaSeguimentoSetResultado } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoSetResultado'
import { hasAnySim } from '@mchat-rf/consulta-seguimento/utils-consultaSeguimento'
import { Flow, isEmpty, Theme, useTheme, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { RadioGroupBoxField } from 'components/form/field/RadioGroupBoxField'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { RefObject } from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import {
  ConsultaSeguimentoPergunta07ApontaPorEnum,
  ConsultaSeguimentoPergunta07Model,
  ConsultaSeguimentoPergunta07OlharParaEnum,
} from './model-consultaSeguimentoPergunta07'
import { PERGUNTA_07_SCROLL_CONFIG } from './scrollConfig-consultaSeguimentoPergunta07'
import {
  calculateResultadoPergunta07,
  conditionToCalculatePergunta07,
  isPergunta07OlharParaAllNao,
} from './utils-consultaSeguimentoPergunta07'

interface ConsultaSeguimentoPergunta07FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta07Model>
  perguntasSelecionadas: MChatPerguntaEnum[]
  containerRef: RefObject<HTMLDivElement>
  goNextButtonRef: RefObject<HTMLButtonElement>
}

export function ConsultaSeguimentoPergunta07Field(props: Readonly<ConsultaSeguimentoPergunta07FieldProps>) {
  const { name, perguntasSelecionadas, containerRef, goNextButtonRef } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta07Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta07Model>({
    name,
    conditionToCalculate: conditionToCalculatePergunta07,
    calculateResultado: calculateResultadoPergunta07,
  })

  const { getId, triggerScrollFor } = useConsultaSeguimentoAutoScroll<ConsultaSeguimentoPergunta07Model>({
    name,
    containerRef,
    goNextButtonRef,
    scrollConfig: PERGUNTA_07_SCROLL_CONFIG,
  })

  const isPergunta6Included = perguntasSelecionadas.includes(MChatPerguntaEnum.PERGUNTA_06)

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        {isPerguntaStatusRespondendo(value.status) && (
          <ConsultaSeguimentoPerguntaHeader
            pergunta={MChatPerguntaEnum.PERGUNTA_07}
            title={
              isPergunta6Included
                ? '“Acabamos de falar sobre o apontar para pedir algo. Mas, e para mostrar algo interessante para você, ele aponta com o dedo?”'
                : '“O seu filho, para mostrar algo interessante para você, ele aponta com o dedo?”'
            }
            description={
              isPergunta6Included
                ? 'Assim que o entrevistador finalizar o item 6,\n deverá PERGUNTAR A TODOS:'
                : undefined
            }
            status={value.status}
            field={
              <SimNaoSwitchButtonField
                name={name.PERGUNTA_07_HEADER}
                onChange={() => triggerScrollFor('PERGUNTA_07_HEADER')}
              />
            }
          />
        )}
        {!isEmpty(value.PERGUNTA_07_HEADER) && (
          <VFlow id={getId('PERGUNTA_07_OLHAR_PARA')}>
            <VFlow vSpacing={0}>
              <ConsultaSeguimentoIndicatorStart />
              {value.PERGUNTA_07_HEADER === SimNaoEnum.SIM && (
                <ConsultaSeguimentoIndicatorHelper
                  title='Por favor, me dê um exemplo de algo que ele possa apontar para mostrar para você.'
                  subtitle='(Se o genitor não der nenhum dos exemplos abaixo, perguntar estes que se seguem:)'
                />
              )}
            </VFlow>
            <VFlow vSpacing={0}>
              <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta07OlharParaEnum>
                name={name.PERGUNTA_07_OLHAR_PARA}
                label='Seu filho alguma vez já quis que você olhasse para algo interessante como...'
                description='Responda os exemplos até pontuar na Pergunta 7 ou avançar para o próximo fluxo'
                options={PERGUNTA_07_OLHAR_PARA_OPTIONS}
                required
                onChange={() => triggerScrollFor('PERGUNTA_07_OLHAR_PARA')}
              />
              {hasAnySim(value.PERGUNTA_07_OLHAR_PARA) && (
                <Flow direction='vertical' gap={0} id={getId('PERGUNTA_07_APONTA_COM_DEDO')}>
                  <ConsultaSeguimentoIndicatorStep text='Sim para qualquer um:' />
                  <PerguntaSimNaoRadioGroupBoxField
                    name={name.PERGUNTA_07_APONTA_COM_DEDO}
                    label='Como é que seu filho chama sua atenção para ele? Ele aponta com o dedo?'
                    required
                    onChange={() => triggerScrollFor('PERGUNTA_07_APONTA_COM_DEDO')}
                  />
                  {value.PERGUNTA_07_APONTA_COM_DEDO === SimNaoEnum.SIM && (
                    <VFlow vSpacing={0} style={styles.apontaComDedo} id={getId('PERGUNTA_07_APONTA_POR')}>
                      <ConsultaSeguimentoArrowDown />
                      <RadioGroupBoxField<ConsultaSeguimentoPergunta07ApontaPorEnum>
                        name={name.PERGUNTA_07_APONTA_POR}
                        label='“Ele aponta para mostrar o interesse dele/dela ou para pedir ajuda?”'
                        options={PERGUNTA_07_APONTA_POR_OPTIONS}
                        labelStyle={styles.apontaComDedoLabel}
                        required
                        onChange={() => triggerScrollFor('PERGUNTA_07_APONTA_POR')}
                      />
                    </VFlow>
                  )}
                </Flow>
              )}
            </VFlow>
          </VFlow>
        )}
      </VFlow>
      {value.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          text={{ FALHOU: isPergunta07OlharParaAllNao(value?.PERGUNTA_07_OLHAR_PARA) ? 'Não para todos:' : undefined }}
          resultado={value.resultado}
        />
      )}
    </VFlow>
  )
}

const PERGUNTA_07_OLHAR_PARA_OPTIONS: RadioOption<ConsultaSeguimentoPergunta07OlharParaEnum>[] = [
  {
    label: 'Um avião no céu?',
    value: ConsultaSeguimentoPergunta07OlharParaEnum.AVIAO,
  },
  {
    label: 'Um caminhão na rua?',
    value: ConsultaSeguimentoPergunta07OlharParaEnum.CAMINHAO,
  },
  {
    label: 'Um bicho no chão?',
    value: ConsultaSeguimentoPergunta07OlharParaEnum.BICHO,
  },
  {
    label: 'Um animal no quintal?',
    value: ConsultaSeguimentoPergunta07OlharParaEnum.ANIMAL,
  },
]

const PERGUNTA_07_APONTA_POR_OPTIONS: RadioOption<ConsultaSeguimentoPergunta07ApontaPorEnum>[] = [
  {
    label: 'Para conseguir ajuda',
    value: ConsultaSeguimentoPergunta07ApontaPorEnum.AJUDA,
  },
  {
    label: 'Tanto para mostrar o interesse dele/dela como para conseguir ajuda',
    value: ConsultaSeguimentoPergunta07ApontaPorEnum.INTERESSE,
  },
]

const createStyles = (theme: Theme) => ({
  apontaComDedo: css`
    & > div:first-of-type {
      align-self: center;
    }
  `,
  apontaComDedoLabel: css`
    color: ${theme.pallete.primary.main};
  `,
})
